// Pure logic for the internal "someone entered" beacon (D4, Step S3, see
// meeting-contract.md section 8). Kept out of Meeting.jsx so the retry loop
// can be unit-tested once this repo has a test runner (it has none today).
//
// This only ever answers "did someone enter", never "is the room empty" —
// that distinction is why it was accepted where a room-emptied beacon was
// rejected elsewhere. Recording entry doesn't care who entered, only that
// someone did.

// Whether to fire the beacon: internal meetings only, and only once per
// mount — a re-render or a second `videoConferenceJoined` (e.g. a rejoin
// after a breakout) must never send it twice.
export const shouldSendMeetingEntered = ({ isInternal, alreadySent }) =>
  Boolean(isInternal && !alreadySent);

// Delay before each of the 3 retries (the first attempt is immediate).
export const MEETING_ENTERED_RETRY_DELAYS_MS = [500, 1500, 3000];

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// POSTs the entry beacon, retrying up to 3 times with backoff on failure.
// Best-effort by design (contract section 8: the route always answers 200 on
// a resolvable token and silently skips the write when it no longer
// applies) — the promise only rejects once every attempt has failed, and
// callers must treat that as "log and move on", never as a reason to block
// or alter the call.
export const sendMeetingEntered = async ({
  fetchFn,
  apiUrl,
  token,
  delaysMs = MEETING_ENTERED_RETRY_DELAYS_MS,
  sleepFn = defaultSleep
}) => {
  const maxAttempts = delaysMs.length + 1;
  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      await sleepFn(delaysMs[attempt - 1]);
    }
    try {
      const response = await fetchFn(`${apiUrl}/api/public/meeting/internal/entered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: token })
      });
      if (response.ok) return true;
      lastError = new Error(`entered beacon failed with status ${response.status}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('entered beacon failed');
};
